import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import Icon from '../../../components/ui/Icon'
import { Button } from '../../../components/buttons/Button'
import { Buttons } from '../../../components/buttons/Buttons'
import { CheckboxInput } from '../../../components/input/checkboxInput/CheckboxInput'
import { ConfirmItemRow } from '../../../components/confirm/confirmItemRow/ConfirmItemRow'

import { useApp } from '../../../contexts/AppContext'
import { useAuth } from '../../../contexts/AuthContext'
import { useConfirm } from '../../../contexts/ConfirmContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { CONFIRM_ABORT, CONFIRM_SKIP_NAVIGATION, ConfirmLayout } from '../../../types/confirm/confirm'

const confirmActionButtonClassName =
  'min-w-0 flex-grow-0 flex-shrink-0 flex-sm-fill w-sm-auto'

export default function Confirm() {
  const navigate = useNavigate()
  const { t } = useTranslation(['exceptions', 'common'])
  const { showBanner } = useApp()
  const { isAuthenticated } = useAuth()
  const { confirmData, clearConfirm } = useConfirm()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localValues, setLocalValues] = useState<Record<string, string>>({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const isNavigatingAwayRef = useRef(false)

  useEffect(() => {
    setTermsAccepted(false)
  }, [confirmData])

  useEffect(() => {
    if (!confirmData?.itemCards) return

    const values: Record<string, string> = {}

    confirmData.itemCards.forEach(item => {
      item.details?.forEach(detail => {
        if (detail.key) {
          values[detail.key] = detail.value
        }
      })
    })

    setLocalValues(currentValues =>
      Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          currentValues[key] ?? value,
        ])
      )
    )
  }, [confirmData])

  const handleBack = useCallback(() => {
    if (!confirmData) {
      navigate(-1)
      return
    }

    isNavigatingAwayRef.current = true

    if (confirmData.onCancel) {
      confirmData.onCancel()
    } else if (confirmData.returnTo) {
      navigate(confirmData.returnTo, { state: confirmData.returnState })
    } else {
      navigate(-1)
    }

    clearConfirm()
  }, [clearConfirm, confirmData, navigate])

  const handleConfirm = async () => {
    if (!confirmData?.onConfirm || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    const returnTo = confirmData.returnTo

    try {
      const result = await confirmData.onConfirm(
        confirmData.itemCards ?? [],
        localValues,
        confirmData.payload
      )

      if (result === CONFIRM_ABORT) {
        return
      }

      isNavigatingAwayRef.current = true
      clearConfirm()

      if (result === CONFIRM_SKIP_NAVIGATION) {
        return
      }

      if (returnTo) {
        navigate(returnTo)
      } else {
        navigate(-1)
      }
    } catch (caughtError) {
      showBanner({
        kind: BannerKind.ERR,
        msg:
          caughtError instanceof Error ? caughtError.message : t('exceptions:unknown'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDetailChange = (key: string, value: string) => {
    setLocalValues(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  useEffect(() => {
    if (
      confirmData &&
      Array.isArray(confirmData.itemCards) &&
      confirmData.itemCards.length === 0
    ) {
      handleBack()
    }
  }, [confirmData, handleBack])

  useEffect(() => {
    if (!confirmData && !isNavigatingAwayRef.current) {
      navigate('/', { replace: true })
    }
  }, [confirmData, navigate])

  if (!confirmData) {
    return null
  }

  const itemCards = confirmData.itemCards ?? []
  const layout = confirmData.layout ?? ConfirmLayout.SUMMARY
  const canConfirm =
    itemCards.length > 0 && (!confirmData.showTermsLink || termsAccepted)

  return (
    <div className="ph-page confirm-page">
      <div className={`summary-card confirm-card confirm-card--${layout.toLowerCase()} p-3 p-sm-4`}>
        <div className="mb-8">
          {confirmData.subtitle && (
            <div className="eyebrow">{confirmData.subtitle}</div>
          )}
          <h3 className="summary-title">
            {confirmData.title ?? t('confirm.title')}
          </h3>
          {confirmData.description && (
            <p className="confirm-description">{confirmData.description}</p>
          )}
        </div>

        {itemCards.map(item => (
          <ConfirmItemRow
            key={item.id}
            item={item}
            layout={layout}
            localValues={localValues}
            onDetailChange={handleDetailChange}
          />
        ))}

        {confirmData.warning && (
          <div className="confirm-warning">{confirmData.warning}</div>
        )}

        {confirmData.footer && (
          <div className="confirm-footer when">{confirmData.footer}</div>
        )}

        {confirmData.summary && (
          <>
            {confirmData.summary.details.length > 0 && (
              <div className="summary-stats ph-col mt-16">
                {confirmData.summary.details.map(detail => (
                  <div className="ph-row ph-between" key={detail.label}>
                    <span className="muted">{detail.label}</span>
                    <span className="mono">{detail.value}</span>
                  </div>
                ))}
              </div>
            )}
            {confirmData.summary.total && (
              <div className="sum-total">
                <div>
                  <div className="eyebrow">
                    {confirmData.summary.total.label}
                  </div>
                </div>
                <div className="value">{confirmData.summary.total.value}</div>
              </div>
            )}
          </>
        )}

        <div className="summary-actions ph-col mt-16">
          {confirmData.showTermsLink && (
            <div className="confirm-terms">
              <CheckboxInput
                id="termsConsent"
                name="termsConsent"
                className="confirm-terms-checkbox"
                checked={termsAccepted}
                disabled={isSubmitting}
                onChange={(_, value) => setTermsAccepted(value)}
                label={
                  <Trans
                    i18nKey="common:confirm.terms"
                    components={{
                      terms: (
                        <Link
                          to={
                            isAuthenticated
                              ? '/account/terms-and-conditions'
                              : '/terms'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="confirm-terms-link"
                          onClick={event => event.stopPropagation()}
                        />
                      ),
                    }}
                  />
                }
              />
            </div>
          )}

          <Buttons className="confirm-actions-buttons flex-sm-row justify-content-sm-end">
            <Button
              label={confirmData.backLabel ?? t('confirm.cancel-label')}
              variant="ghost"
              size="md"
              block
              className={confirmActionButtonClassName}
              disabled={isSubmitting}
              onClick={handleBack}
            />
            <Button
              label={confirmData.confirmLabel ?? t('confirm.confirm-label')}
              icon={<Icon name="arrowR" size={16} />}
              iconPosition="right"
              size="md"
              block
              className={confirmActionButtonClassName}
              loading={isSubmitting}
              disabled={!canConfirm || isSubmitting}
              onClick={handleConfirm}
            />
          </Buttons>
        </div>
      </div>
    </div>
  )
}
